import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { errorLogger, ErrorCategory, ErrorSeverity } from '../utils/errorLogger';

// Solana Wallet Types
export type SolanaWalletName = 'Phantom' | 'Solflare' | 'MetaMask Snap';

export interface SolanaWallet {
  name: SolanaWalletName;
  icon: string;
  description: string;
  adapter: any;
}

export interface SolanaWalletState {
  connected: boolean;
  publicKey: string | null;
  walletName: SolanaWalletName | null;
  connecting: boolean;
  error: string | null;
}

export interface SolanaContextType extends SolanaWalletState {
  connect: (walletName: SolanaWalletName) => Promise<string>;
  disconnect: () => Promise<void>;
  signMessage: (message: Uint8Array) => Promise<Uint8Array | null>;
  signTransaction: (transaction: any) => Promise<any | null>;
  isWalletInstalled: (walletName: SolanaWalletName) => boolean;
}

// Solana provider types
interface PhantomProvider {
  isPhantom?: boolean;
  connect: () => Promise<{ publicKey: { toString: () => string } }>;
  disconnect: () => Promise<void>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  signTransaction: (transaction: any) => Promise<any>;
  signAllTransactions: (transactions: any[]) => Promise<any[]>;
  on: (event: string, callback: (args: any) => void) => void;
  removeListener: (event: string, callback: (args: any) => void) => void;
  publicKey?: { toString: () => string };
  isConnected?: boolean;
}

interface SolflareProvider {
  isSolflare?: boolean;
  connect: () => Promise<{ publicKey: { toString: () => string } }>;
  disconnect: () => Promise<void>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  signTransaction: (transaction: any) => Promise<any>;
  signAllTransactions: (transactions: any[]) => Promise<any[]>;
  on: (event: string, callback: (args: any) => void) => void;
  removeListener: (event: string, callback: (args: any) => void) => void;
  publicKey?: { toString: () => string };
  isConnected?: boolean;
}

// Extend Window interface only for phantom and solflare
declare global {
  interface Window {
    phantom?: {
      solana?: PhantomProvider;
    };
    solflare?: SolflareProvider;
  }
}

const SolanaContext = createContext<SolanaContextType | undefined>(undefined);

export const useSolanaWallet = () => {
  const context = useContext(SolanaContext);
  if (!context) {
    throw new Error('useSolanaWallet must be used within SolanaWalletProvider');
  }
  return context;
};

interface SolanaWalletProviderProps {
  children: ReactNode;
}

export const SolanaWalletProvider: React.FC<SolanaWalletProviderProps> = ({ children }) => {
  const [state, setState] = useState<SolanaWalletState>({
    connected: false,
    publicKey: null,
    walletName: null,
    connecting: false,
    error: null,
  });

  const getProvider = useCallback((walletName: SolanaWalletName): PhantomProvider | SolflareProvider | any | null => {
    const win = window as any;
    switch (walletName) {
      case 'Phantom':
        return win.phantom?.solana || null;
      case 'Solflare':
        return win.solflare || null;
      case 'MetaMask Snap':
        return win.ethereum || null;
      default:
        return null;
    }
  }, []);

  const isWalletInstalled = useCallback((walletName: SolanaWalletName): boolean => {
    if (typeof window === 'undefined') return false;
    const win = window as any;

    switch (walletName) {
      case 'Phantom':
        return !!win.phantom?.solana?.isPhantom;
      case 'Solflare':
        return !!win.solflare?.isSolflare;
      case 'MetaMask Snap':
        return !!win.ethereum?.isMetaMask;
      default:
        return false;
    }
  }, []);

  const checkMetaMaskSnapSupport = useCallback(async (): Promise<boolean> => {
    try {
      const win = window as any;
      const ethereum = win.ethereum;
      if (!ethereum) return false;
      
      const snaps = await ethereum.request({
        method: 'wallet_getSnaps'
      });
      
      const solanaSnapIds = [
        'npm:@solana-labs/snap',
        'npm:@solflare-wallet/solana-snap',
        'local:http://localhost:8080'
      ];
      
      return Object.keys(snaps || {}).some((snapId: string) =>
        snapId.includes('solana') || solanaSnapIds.includes(snapId)
      );
    } catch (error) {
      console.log('MetaMask Snap check failed:', error);
      return false;
    }
  }, []);

  const connectPhantom = useCallback(async (): Promise<string> => {
    const win = window as any;
    const provider = win.phantom?.solana;
    if (!provider) {
      throw new Error('Phantom wallet not found. Please install Phantom extension.');
    }

    try {
      if (provider.isConnected && provider.publicKey) {
        return provider.publicKey.toString();
      }

      const response = await provider.connect();
      return response.publicKey.toString();
    } catch (error: any) {
      if (error.code === 4001) {
        throw new Error('User rejected the connection request.');
      }
      throw error;
    }
  }, []);

  const connectSolflare = useCallback(async (): Promise<string> => {
    const win = window as any;
    const provider = win.solflare;
    if (!provider) {
      throw new Error('Solflare wallet not found. Please install Solflare extension.');
    }

    try {
      if (provider.isConnected && provider.publicKey) {
        return provider.publicKey.toString();
      }

      const response = await provider.connect();
      return response.publicKey.toString();
    } catch (error: any) {
      if (error.code === 4001) {
        throw new Error('User rejected the connection request.');
      }
      throw error;
    }
  }, []);

  const connectMetaMaskSnap = useCallback(async (): Promise<string> => {
    const win = window as any;
    const ethereum = win.ethereum;
    if (!ethereum?.isMetaMask) {
      throw new Error('MetaMask not found. Please install MetaMask extension.');
    }

    try {
      const accounts = await ethereum.request({ method: 'eth_accounts' });
      if (!accounts || accounts.length === 0) {
        await ethereum.request({ method: 'eth_requestAccounts' });
      }

      const hasSnapSupport = await checkMetaMaskSnapSupport();
      
      if (!hasSnapSupport) {
        const snapId = 'npm:@solflare-wallet/solana-snap';
        try {
          await ethereum.request({
            method: 'wallet_requestSnaps',
            params: {
              [snapId]: {}
            }
          });
        } catch (snapError: any) {
          throw new Error('Failed to install Solana Snap. Please install it manually from snaps.metamask.io');
        }
      }

      const solanaAddress = await ethereum.request({
        method: 'wallet_invokeSnap',
        params: {
          snapId: 'npm:@solflare-wallet/solana-snap',
          request: {
            method: 'getAddress'
          }
        }
      });

      if (!solanaAddress) {
        throw new Error('Failed to get Solana address from MetaMask Snap.');
      }

      return solanaAddress;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new Error('User rejected the connection request.');
      }
      throw error;
    }
  }, [checkMetaMaskSnapSupport]);

  const connect = useCallback(async (walletName: SolanaWalletName): Promise<string> => {
    setState(prev => ({ ...prev, connecting: true, error: null }));

    try {
      let publicKey: string;

      switch (walletName) {
        case 'Phantom':
          publicKey = await connectPhantom();
          break;
        case 'Solflare':
          publicKey = await connectSolflare();
          break;
        case 'MetaMask Snap':
          publicKey = await connectMetaMaskSnap();
          break;
        default:
          throw new Error(`Unsupported wallet: ${walletName}`);
      }

      setState({
        connected: true,
        publicKey,
        walletName,
        connecting: false,
        error: null,
      });

      localStorage.setItem('solanaWalletName', walletName);
      localStorage.setItem('solanaPublicKey', publicKey);

      console.log('Solana wallet connected', { walletName, publicKey: publicKey.slice(0, 10) + '...' });
      
      return publicKey;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to connect wallet';
      setState(prev => ({
        ...prev,
        connected: false,
        publicKey: null,
        walletName: null,
        connecting: false,
        error: errorMessage,
      }));

      errorLogger.logError(
        ErrorCategory.WALLET_CONNECTION,
        ErrorSeverity.MEDIUM,
        errorMessage,
        'Failed to connect Solana wallet',
        { walletName },
        error,
        true
      );
      throw error;
    }
  }, [connectPhantom, connectSolflare, connectMetaMaskSnap]);

  const disconnect = useCallback(async () => {
    try {
      const { walletName } = state;
      
      if (walletName && walletName !== 'MetaMask Snap') {
        const provider = getProvider(walletName) as PhantomProvider | SolflareProvider | null;
        if (provider && 'disconnect' in provider) {
          await provider.disconnect();
        }
      }

      setState({
        connected: false,
        publicKey: null,
        walletName: null,
        connecting: false,
        error: null,
      });

      localStorage.removeItem('solanaWalletName');
      localStorage.removeItem('solanaPublicKey');

      console.log('Solana wallet disconnected', { walletName });
    } catch (error: any) {
      errorLogger.logError(
        ErrorCategory.WALLET_CONNECTION,
        ErrorSeverity.LOW,
        error.message,
        'Failed to disconnect Solana wallet',
        {},
        error,
        true
      );
    }
  }, [state, getProvider]);

  const signMessage = useCallback(async (message: Uint8Array): Promise<Uint8Array | null> => {
    try {
      const { walletName } = state;
      if (!walletName) throw new Error('Wallet not connected');

      if (walletName === 'MetaMask Snap') {
        const win = window as any;
        const result = await win.ethereum?.request({
          method: 'wallet_invokeSnap',
          params: {
            snapId: 'npm:@solflare-wallet/solana-snap',
            request: {
              method: 'signMessage',
              params: {
                message: Array.from(message)
              }
            }
          }
        });
        return result ? new Uint8Array(result.signature) : null;
      }

      const provider = getProvider(walletName) as PhantomProvider | SolflareProvider | null;
      if (!provider || !('signMessage' in provider)) {
        throw new Error('Provider does not support message signing');
      }

      const result = await provider.signMessage(message);
      return result.signature;
    } catch (error: any) {
      errorLogger.logError(
        ErrorCategory.WALLET_CONNECTION,
        ErrorSeverity.MEDIUM,
        error.message,
        'Failed to sign message',
        {},
        error,
        true
      );
      return null;
    }
  }, [state, getProvider]);

  const signTransaction = useCallback(async (transaction: any): Promise<any | null> => {
    try {
      const { walletName } = state;
      if (!walletName) throw new Error('Wallet not connected');

      if (walletName === 'MetaMask Snap') {
        const win = window as any;
        const result = await win.ethereum?.request({
          method: 'wallet_invokeSnap',
          params: {
            snapId: 'npm:@solflare-wallet/solana-snap',
            request: {
              method: 'signTransaction',
              params: {
                transaction: transaction.serialize ? Array.from(transaction.serialize()) : transaction
              }
            }
          }
        });
        return result;
      }

      const provider = getProvider(walletName) as PhantomProvider | SolflareProvider | null;
      if (!provider || !('signTransaction' in provider)) {
        throw new Error('Provider does not support transaction signing');
      }

      return await provider.signTransaction(transaction);
    } catch (error: any) {
      errorLogger.logError(
        ErrorCategory.SWAP_TRANSACTION,
        ErrorSeverity.MEDIUM,
        error.message,
        'Failed to sign transaction',
        {},
        error,
        true
      );
      return null;
    }
  }, [state, getProvider]);

  // Auto-connect on mount if previously connected
  useEffect(() => {
    const autoConnect = async () => {
      const savedWalletName = localStorage.getItem('solanaWalletName') as SolanaWalletName | null;
      const savedPublicKey = localStorage.getItem('solanaPublicKey');

      if (savedWalletName && savedPublicKey && isWalletInstalled(savedWalletName)) {
        try {
          const provider = getProvider(savedWalletName) as PhantomProvider | SolflareProvider | null;
          if (provider && savedWalletName !== 'MetaMask Snap') {
            const isStillConnected = ('isConnected' in provider && provider.isConnected) || 
                                     ('publicKey' in provider && provider.publicKey);
            if (isStillConnected) {
              setState({
                connected: true,
                publicKey: savedPublicKey,
                walletName: savedWalletName,
                connecting: false,
                error: null,
              });
            }
          }
        } catch (error) {
          localStorage.removeItem('solanaWalletName');
          localStorage.removeItem('solanaPublicKey');
        }
      }
    };

    autoConnect();
  }, [getProvider, isWalletInstalled]);

  // Listen for account changes
  useEffect(() => {
    const handleAccountChange = (publicKey: any) => {
      if (publicKey) {
        setState(prev => ({
          ...prev,
          publicKey: publicKey.toString(),
        }));
      } else {
        setState({
          connected: false,
          publicKey: null,
          walletName: null,
          connecting: false,
          error: null,
        });
        localStorage.removeItem('solanaWalletName');
        localStorage.removeItem('solanaPublicKey');
      }
    };

    if (state.walletName && state.walletName !== 'MetaMask Snap') {
      const provider = getProvider(state.walletName) as PhantomProvider | SolflareProvider | null;
      if (provider && 'on' in provider) {
        provider.on('accountChanged', handleAccountChange);
        return () => {
          if ('removeListener' in provider) {
            provider.removeListener('accountChanged', handleAccountChange);
          }
        };
      }
    }
  }, [state.walletName, getProvider]);

  const value: SolanaContextType = {
    ...state,
    connect,
    disconnect,
    signMessage,
    signTransaction,
    isWalletInstalled,
  };

  return (
    <SolanaContext.Provider value={value}>
      {children}
    </SolanaContext.Provider>
  );
};

export default SolanaContext;